import React, {
  ChangeEvent,
  memo,
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  styled,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Paper,
  Dialog,
  OutlinedInput,
  InputAdornment,
  Select,
  TablePagination,
  type SelectChangeEvent,
  Snackbar,
  LinearProgress,
  Alert,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  DataGridPro,
  type GridRowParams,
  type GridCellParams,
  type GridColDef,
} from '@mui/x-data-grid-pro';
import { CancelOutlined, ExpandMore, Search } from '@mui/icons-material';
import { LocalizationProvider, TimeField } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';

import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';

import type {
  TripLegAttestationItem,
  TripLegResponseItem,
} from '../../../data/models/trip-leg.model';
import { TripLegExceptionBC } from '../../../data/models/trip-leg-exception.model';
import {
  TripLegStatusBC,
  TripLegStatus,
  tripStatusAttestationList,
  tripStatusNoLoadList,
} from '../../../data/models/trip-leg-status.model';
import {
  selectTimeZones,
  selectTripLegExceptions,
} from '../../../data/reference.slice';
import {
  selectTripLegError,
  clearException,
  setTripLegError,
  updateTripLegDriverAsync,
  updateTripLegLogAsync,
  clearIsUnderDiscussionAsync,
  setExceptions,
  upsertTripLegException,
  removeTripLegLogEntry,
  selectUpdatingTripLegLogID,
  selectUpdatingTripLegLogStatus,
  fetchTripLegsV2Async,
  overrideCompletedStatusAndUpdateLog,
} from '../../../data/trip.slice';
import {
  selectDriverList,
  fetchDriverListAsync,
} from '../../../data/driver.slice';

import { attestationUtils } from '../../../services/attestationUtils';
import DateUtils from '../../../services/DateUtils';
import { financialUtils } from '../../../services/financialUtils';

import { FullScreenDialogContent } from '../../dialogs/FullScreenDialogContent';
import { FullScreenDialogTitle } from '../../dialogs/FullScreenDialogTitle';
import { StatusDialog } from '../../dialogs/StatusDialog';
import PriceTooltip from './PriceTooltipAsync';
import DiscussionDialog from './DiscussionDialog';
import DialogTransition from '../DialogTransition';
import { DateTime } from 'luxon';
import { useOptimisticUpdates } from '../../../hooks/useOptimisticUpdates';
import { transformTripLegToRow } from '../../../utils/tripLegRowTransformer';
import {
  checkTimestampsFilled,
  checkTripLegReadyToInvoice,
} from '../../../utils/timestampValidation';
import {
  processTimestampUpdate,
  type TimestampUpdateParams,
} from '../../../utils/timestampUpdateHandler';

const PREFIX = 'NeedsAttention';

const classes = {
  container: `${PREFIX}-container`,
  grid: `${PREFIX}-grid`,
  header: `${PREFIX}-header`,
  closeButton: `${PREFIX}-closeButton`,
  gridContainer: `${PREFIX}-gridContainer`,
  rowHover: `${PREFIX}-rowHover`,
  bottomCloseButton: `${PREFIX}-button`,
  moveToAttestationButton: `${PREFIX}-moveToAttestationButton`,
  bottomButtonContainer: `${PREFIX}-bottomButtonContainer`,
  toolbar: `${PREFIX}-toolbar`,
  toolContainer: `${PREFIX}-toolContainer`,
  toolInput: `${PREFIX}-toolInput`,
  cellContent: `${PREFIX}-cellContent`,
  primaryText: `${PREFIX}-primaryText`,
  secondaryText: `${PREFIX}-secondaryText`,
  renderTime: `${PREFIX}-renderTime`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.closeButton}`]: {
    position: 'absolute',
    right: '8px',
    top: '8px',
  },
  [`& .${classes.container}`]: {
    width: '100%',
    maxWidth: 1632,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  [`& .${classes.header}`]: {
    display: 'flex',
    height: '168px',
    padding: '48px 0px 24px 0px',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '24px',
    alignSelf: 'stretch',
  },
  [`& .${classes.gridContainer}`]: {
    padding: '32px',
  },
  [`& .${classes.bottomCloseButton}`]: {
    display: 'flex',
    width: '144px',
    height: '48px',
    minWidth: '144px',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  [`& .${classes.moveToAttestationButton}`]: {
    display: 'flex',
    width: '171px',
    height: '48px',
    minWidth: '144px',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  [`& .${classes.toolbar}`]: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',

    [`& input.MuiInputBase-input::placeholder`]: {
      color: 'rgba(1, 16, 18, 0.60)',
      opacity: 1,
    },
  },
  [`& .${classes.toolContainer}`]: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '24px',
    marginBottom: '16px',
  },
  [`& .${classes.toolInput}`]: {
    borderColor: 'transparent !important',
    borderRadius: '3px',
    background: '#FFF',
    boxShadow: '0px 2px 7px 0px rgba(1, 16, 18, 0.15)',
    fontFamily: 'Source Sans Pro',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '24px',
    letterSpacing: '0.15px',
  },
  [`& .${classes.toolInput} fieldset`]: {
    display: 'none',
  },
  [`& .${classes.toolInput} input`]: {
    color: 'rgba(1, 16, 18, 0.60)',
    padding: '12px',
    paddingRight: 0,

    '&::placeholder': {
      color: 'rgba(1, 16, 18, 0.60)',
    },
  },
  [`& .${classes.toolInput} .MuiSelect-select`]: {
    padding: '12px',
    color: 'rgba(1, 16, 18, 0.60)',
  },
  [`& .${classes.grid}`]: {
    border: 'none',
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '143%',
    letterSpacing: '0.17px',

    '& .MuiDataGrid-virtualScrollerRenderZone': {
      zIndex: 1000,
      paddingTop: 8,
    },

    '& .MuiDataGrid-row': {
      letterSpacing: '0.17px',
    },

    '& .MuiDataGrid-columnHeaderTitleContainer, & .MuiDataGrid-columnHeaderTitleContent, & .MuiDataGrid-columnHeaderTitleContainerContent, & .MuiDataGrid-columnHeaderTitle':
      {
        width: '100%',
        color: 'rgba(1, 16, 18, 0.90)',

        fontFamily: 'Source Sans Pro',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: '600',
        lineHeight: '24px',
        letterSpacing: '0.17px',
      },

    '& .MuiDataGrid-row--borderBottom .MuiDataGrid-columnHeader': {
      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    },
    '& .MuiDataGrid-columnSeparator': {
      display: 'none',
    },

    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },

    '& .MuiDataGrid-cell': {
      '&.MuiDataGrid-cellCheckbox': {
        overflow: 'visible',

        '& .MuiButtonBase-root': {
          padding: '8px',
          marginLeft: '-6px',
          '& .MuiSvgIcon-root': {
            width: '24px !important',
            height: '24px !important',
          },
        },

        '& .MuiCheckbox-root.Mui-disabled': {
          position: 'relative',
          overflow: 'visible',
          pointerEvents: 'auto',
          cursor: 'pointer',
        },

        '& .MuiCheckbox-root.Mui-disabled::before': {
          content: `'Add missing trip info'`,
          position: 'absolute',
          fontFamily: 'Source Sans Pro',
          fontStyle: 'normal',
          fontSize: 10,
          fontWeight: 600,
          lineHeight: '14px',
          bottom: 36,
          left: 8,
          background: 'rgba(1, 16, 18, 0.90)',
          color: 'white',
          padding: '4px 8px',
          borderRadius: 4,
          boxShadow:
            '0px 5px 5px -3px rgba(0, 0, 0, 0.10), 0px 8px 10px 1px rgba(0, 0, 0, 0.07), 0px 3px 14px 2px rgba(0, 0, 0, 0.06)',

          opacity: 0,
          visibility: 'hidden',
          transition: 'opacity 0.3s ease-in-out',
        },

        '& .MuiCheckbox-root.Mui-disabled::after': {
          content: `''`,
          position: 'absolute',
          bottom: 22,
          left: 14,
          borderWidth: 6,
          borderTopWidth: 8,
          borderStyle: 'solid',
          borderColor:
            'rgba(1, 16, 18, 0.90) transparent transparent transparent',
          opacity: 0,
          visibility: 'hidden',
          transition: 'opacity 0.3s ease-in-out',
        },

        '& .MuiCheckbox-root.Mui-disabled:hover::before': {
          opacity: 1,
          visibility: 'visible',
        },
        '& .MuiCheckbox-root.Mui-disabled:hover::after': {
          opacity: 1,
          visibility: 'visible',
        },
      },
    },

    '& .MuiDataGrid-columnHeaderCheckbox': {
      width: '32px !important',
    },

    '& .MuiDataGrid-cellCheckbox': {
      '--width': '44px !important',
      marginRight: '-12px !important',
    },

    '& .MuiDataGrid-cell:nth-last-child(2)': {
      flex: 1,
    },

    [`& .MuiDataGrid-row:not(:hover):not(.${classes.rowHover})`]: {
      '& .MuiDataGrid-cell:last-child': {
        position: 'absolute',
        right: 0,
        opacity: 0,
      },

      [`& .${classes.cellContent} input::placeholder`]: {
        // color: 'transparent',
      },
      [`& .${classes.cellContent}.Mui-disabled input::placeholder`]: {
        // color: 'transparent',
      },
      [`& .${classes.cellContent} fieldset`]: {
        display: 'none',
      },
    },

    [`& .MuiDataGrid-row.${classes.rowHover}, & .MuiDataGrid-row:hover`]: {
      background:
        'linear-gradient(0deg, rgba(1, 16, 18, 0.04) 0%, rgba(1, 16, 18, 0.04) 100%), #FFF',

      '& .MuiInputBase-input': {
        backgroundColor: 'white',
      },
    },

    '& .MuiDataGrid-row:focus-within': {
      [`& .${classes.cellContent} input::placeholder`]: {
        color: 'rgba(1, 16, 18, 0.60)',
        opacity: 1,
      },
      [`& .${classes.cellContent} fieldset`]: {
        display: 'inherit',
      },
    },

    [`& .${classes.cellContent} .MuiOutlinedInput-root`]: {
      paddingRight: 0,
    },

    [`& .${classes.cellContent} input`]: {
      padding: '8px',
      textAlign: 'center',
      color: 'rgba(1, 16, 18, 0.90)',
      fontFamily: 'Source Sans Pro',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: '143%',
      letterSpacing: '0.17px',
    },
  },
  [`& .${classes.cellContent}`]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: '100%',
  },
  [`& .${classes.primaryText}`]: {
    color: 'rgba(1, 16, 18, 0.90)',
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '143%',
    letterSpacing: '0.17px',
  },
  [`& .${classes.secondaryText}`]: {
    color: 'rgba(1, 16, 18, 0.60)',
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '143%',
  },
  [`& .${classes.renderTime}`]: {
    color: 'rgba(1, 16, 18, 0.60)',
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '143%',
    textAlign: 'center !important',
    marginTop: -5,
    '&.has-label input': {
      color: 'rgba(1, 16, 18, 0.60) !important',
    },
  },

  [`& .${classes.bottomButtonContainer}`]: {
    display: 'flex',
    alignItems: 'center',
  },

  [`& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-input`]:
    {
      fontSize: '12px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: '166%',
      letterSpacing: '0.4px',
    },

  [`& .MuiTablePagination-selectLabel`]: {
    color: 'rgba(1, 16, 18, 0.60)',
  },
  [`& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows`]: {
    fontFamily: 'Source Sans Pro',
  },
  [`& .MuiTablePagination-input`]: {
    fontFamily: 'Roboto',

    [`& .MuiSelect-select`]: {
      paddingBottom: 0,
    },
  },
}));

const StyledMenu = styled(Menu)(() => ({
  [`& .MuiPaper-root`]: {
    width: '160px',
    marginLeft: '10px',
    borderRadius: '3px',
    boxShadow:
      '0px 5px 5px -3px rgba(0, 0, 0, 0.10), 0px 8px 10px 1px rgba(0, 0, 0, 0.07), 0px 3px 14px 2px rgba(0, 0, 0, 0.06)',

    [`& .MuiList-root`]: {
      padding: 0,
    },
    [`& .MuiMenuItem-root`]: {
      color: 'rgba(1, 16, 18, 0.60)',
      fontFamily: 'Source Sans Pro',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: '143%',
      letterSpacing: '0.17px',
      padding: '8px 16px',
    },
  },
}));

const StyledSelect = styled(Select)({
  outline: 'none !important',

  [`& .MuiSelect-select`]: {
    padding: '8px !important',
    color: 'rgba(1, 16, 18, 0.90)',
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '143%',
    letterSpacing: '0.17px',
    display: 'flex',
    alignItems: 'center',
  },

  [`& svg`]: {
    visibility: 'hidden',
  },
});

const StyledMenuItem = styled(MenuItem)({
  color: 'rgba(1, 16, 18, 0.90)',
  fontFamily: 'Source Sans Pro',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: '400',
  lineHeight: '143%',
  letterSpacing: '0.17px',
});

const StyledCancelOutlined = styled(CancelOutlined)({
  color: 'rgba(1, 16, 18, 0.70)',
  width: '22px',
  height: '22px',
  marginLeft: '8px',
});

type TimeFieldCellProps = {
  value: DateTime | undefined;
  onBlur: React.FocusEventHandler<HTMLInputElement>;
  slotProps?: {
    textField?: {
      onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
      inputProps?: Record<string, unknown>;
    };
  };
  'data-timefield-row'?: number | string;
  'data-timefield-name'?: string;
};

function TimeFieldCell({ value, onBlur, ...rest }: TimeFieldCellProps) {
  const [localValue, setLocalValue] = useState<DateTime | null>(value ?? null);
  const valueISO = value?.toISO() ?? null;

  useEffect(() => {
    setLocalValue(value ?? null);
  }, [valueISO]);

  return (
    <TimeField
      value={localValue}
      onChange={(newValue: DateTime | null) => setLocalValue(newValue)}
      onBlur={onBlur}
      {...(rest as object)}
    />
  );
}

interface NeedsAttentionDialogProps {
  open: boolean;
  onClose: () => void;
  providerId: number;
  tripLegs: TripLegResponseItem[];
}

function NeedsAttentionDialog({
  open,
  onClose,
  providerId,
  tripLegs,
}: NeedsAttentionDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [discussionDialogOpen, setdiscussionDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [originalStatus, setOriginalStatus] = useState<TripLegStatusBC | null>(
    null,
  );
  const [targetStatus, setTargetStatus] = useState<TripLegStatus>(
    TripLegStatus.CanceledLate,
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [exceptionFilter, setExceptionFilter] = useState('View all');
  const [cellResetCounters, setCellResetCounters] = useState<
    Map<string, number>
  >(new Map());
  const [menuOpenRowId, setMenuOpenRowId] = useState<number | null>(null);
  const [selectedTrips, setSelectedTrips] = useState<number[]>([]);
  const [selectedTripLeg, setSelectedTripLeg] =
    useState<TripLegAttestationItem | null>(null);
  const [selectedTripLegStatus, setSelectedTripLegStatus] =
    useState<TripLegAttestationItem | null>(null);

  // Use custom hook for optimistic updates
  const {
    optimisticTimestamps,
    optimisticStatuses,
    updateOptimisticTimestamp,
    updateOptimisticStatus,
    clearOptimisticTimestampsForTrip,
  } = useOptimisticUpdates(tripLegs);

  const lastOptimisticTripRef = useRef<string | null>(null);
  const lastCommittedLogsRef = useRef<
    { tripLegReferenceID: string; status: TripLegStatusBC }[]
  >([]);

  const tripLegExceptionItems = useAppSelector(selectTripLegExceptions);
  const updatingTripLegLogID = useAppSelector(selectUpdatingTripLegLogID);
  const updatingTripLegLogStatus = useAppSelector(
    selectUpdatingTripLegLogStatus,
  );

  const exception = tripLegExceptionItems.find(
    (item) => item.tripLegExceptionBC === TripLegExceptionBC.ReadyInvoice,
  );

  const timezones = useAppSelector(selectTimeZones);

  const getExceptionMessage = (tripLeg: TripLegAttestationItem) => {
    if (tripLeg.isUnderDiscussion) {
      return t('needsAttention.underDiscussion');
    }

    return checkTripLegReadyToInvoice(tripLeg)
      ? t('needsAttention.readyToInvoice')
      : t('needsAttention.missingInfo');
  };

  useEffect(() => {
    if (providerId) {
      dispatch(
        fetchDriverListAsync(1000, 1, 'active', '', null, true, providerId),
      );
    }
  }, [providerId, page, pageSize]);

  const drivers = useAppSelector(selectDriverList);
  const tripLegError = useAppSelector(selectTripLegError);

  // When the API call fails, revert optimistic timestamp and committed logs
  // so the fields return to blank/previous values.
  useEffect(() => {
    if (tripLegError && lastOptimisticTripRef.current) {
      clearOptimisticTimestampsForTrip(lastOptimisticTripRef.current);
      lastCommittedLogsRef.current.forEach(({ tripLegReferenceID, status }) => {
        dispatch(removeTripLegLogEntry({ tripLegReferenceID, status }));
      });
      const affectedTripLeg = tripLegs.find(
        (tl) => tl.tripLegReferenceID === lastOptimisticTripRef.current,
      );
      if (affectedTripLeg?.tripLegID) {
        const tripLegID = affectedTripLeg.tripLegID;
        setCellResetCounters((prev) => {
          const next = new Map(prev);
          ['onSite', 'onBoard', 'completed'].forEach((f) => {
            const key = `${tripLegID}-${f}`;
            next.set(key, (prev.get(key) ?? 0) + 1);
          });
          return next;
        });
      }
      lastCommittedLogsRef.current = [];
      lastOptimisticTripRef.current = null;
    }
  }, [tripLegError]);

  const updateTripLegDriver = (
    driverID: number,
    tripLegReferenceID: string,
  ) => {
    const updateOnly = true;
    dispatch(
      updateTripLegDriverAsync(
        driverID,
        tripLegReferenceID,
        providerId,
        updateOnly,
      ),
    );
  };

  const tripLegsRows = useMemo(() => {
    return tripLegs.map((tripLeg: TripLegResponseItem) => {
      // Use the transformer utility to convert trip leg to row
      const row = transformTripLegToRow(
        tripLeg,
        timezones,
        optimisticTimestamps,
        optimisticStatuses,
        t,
      );

      // Add the exception message specific to this component
      return {
        ...row,
        exception: getExceptionMessage(row),
      };
    });
  }, [tripLegs, timezones, optimisticTimestamps, optimisticStatuses, t]);

  let filteredTrips: TripLegAttestationItem[] = useMemo(() => {
    const normalizedSearch = searchQuery.toLowerCase();
    return tripLegsRows.filter((tripLeg) => {
      const fullPickupAddress = `${tripLeg.pickUpAddressLine1 ?? ''} ${
        tripLeg.pickUpCity ?? ''
      }${tripLeg.pickUpStateCode ? `, ${tripLeg.pickUpStateCode}` : ''}${
        tripLeg.pickUpZipCode ? `, ${tripLeg.pickUpZipCode}` : ''
      }`;
      const fullDropoffAddress = `${tripLeg.dropOffAddressLine1 ?? ''} ${
        tripLeg.dropOffCity ?? ''
      } ${tripLeg.dropOffStateCode ? `, ${tripLeg.dropOffStateCode}` : ''} ${
        tripLeg.dropOffZipCode ? `, ${tripLeg.dropOffZipCode}` : ''
      }`;
      const matchesSearch = [
        tripLeg.tripLegID,
        tripLeg.tripLegReferenceID,
        tripLeg.pickUpDueTimeUTC,
        tripLeg.pickUpAddressLine1,
        tripLeg.pickUpCity,
        fullPickupAddress,
        tripLeg.pickUpStateCode,
        tripLeg.pickUpZipCode,
        tripLeg.dropOffAddressLine1,
        tripLeg.dropOffCity,
        fullDropoffAddress,
        tripLeg.dropOffStateCode,
        tripLeg.dropOffZipCode,
        tripLeg.driverFirstName,
        tripLeg.driverName,
        tripLeg.tripLegExceptionBC,
        tripLeg.exception,
      ].some(
        (field) => field?.toString().toLowerCase().includes(normalizedSearch),
      );

      const matchesException =
        exceptionFilter === 'View all' || tripLeg.exception === exceptionFilter;

      return matchesSearch && matchesException;
    });
  }, [tripLegsRows, searchQuery, exceptionFilter]);

  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedTrips = filteredTrips.slice(startIndex, endIndex);

  const updateTripsWithTimestamp = (
    tripLegReferenceID: string,
    status: TripLegStatusBC,
    time: string,
  ) => {
    const newFilteredTrips = filteredTrips.map((filteredTrip) => {
      const trip = {
        ...filteredTrip,
        tripLeg: {
          ...filteredTrip,
          tripLegStatusLogs: filteredTrip.tripLegStatusLogs?.map(
            (tripLegStatusLog) => ({
              ...tripLegStatusLog,
              createdOnUTC: time,
            }),
          ),
        },
      };
      return trip;
    });
    filteredTrips = newFilteredTrips;
  };

  const openMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
    rowId: number,
    row: TripLegAttestationItem,
  ) => {
    setAnchorEl(event.currentTarget);
    setMenuOpenRowId(rowId);
    setSelectedTripLeg(row);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setMenuOpenRowId(null);
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleExceptionFilterChange = (event: SelectChangeEvent) => {
    setExceptionFilter(event.target.value);
  };

  const getTripById = (tripId: number) => {
    return filteredTrips.find((ft) => ft.tripLegID === tripId);
  };

  const canDoReadyToInvoice = (rowId: number): boolean => {
    const trip = getTripById(rowId);
    const isMissingInfo = trip?.exception === 'Missing Info';
    const isUnderDiscussion = Boolean(trip?.isUnderDiscussion);
    return !isMissingInfo;
  };

  const canSelectRow = (params: GridRowParams<TripLegAttestationItem>) => {
    const { row } = params;
    return !row.isUnderDiscussion && checkTripLegReadyToInvoice(row);
  };

  const handleReadyToInvoice = (rowId: number) => {
    const trip = getTripById(rowId);
    dispatch(clearIsUnderDiscussionAsync(trip?.tripLegID ?? 0, providerId));
  };

  const renderPickupTime = (params: GridCellParams): ReactElement => {
    const { row } = params;
    return (
      <Box className={classes.cellContent}>
        <Typography className={classes.primaryText}>
          {row.pickupTime}
        </Typography>
        <Typography className={classes.secondaryText}>{row.tripID}</Typography>
      </Box>
    );
  };

  const renderPickupAddress = (params: GridCellParams): ReactElement => {
    const { row } = params;

    return (
      <Box className={classes.cellContent}>
        <Typography className={classes.primaryText}>
          {row.pickupAddress}
        </Typography>
        <Typography className={classes.secondaryText}>
          {row.pickupCity}, {row.pickupState}, {row.pickupZipCode}
        </Typography>
      </Box>
    );
  };

  const renderDropoffAddress = (params: GridCellParams): ReactElement => {
    const { row } = params;
    return (
      <Box className={classes.cellContent}>
        <Typography className={classes.primaryText}>
          {row.dropoffAddress}
        </Typography>
        <Typography className={classes.secondaryText}>
          {row.dropoffCity}, {row.dropoffState}, {row.dropoffZipCode}
        </Typography>
      </Box>
    );
  };

  const renderMember = (params: GridCellParams): ReactElement => {
    const { row } = params;
    const memberName = attestationUtils.getPassengerName(row);
    return (
      <Box className={classes.cellContent}>
        <Typography className={classes.primaryText}>{memberName}</Typography>
      </Box>
    );
  };

  const renderDriver = (params: GridCellParams): ReactElement => {
    const { row } = params;
    return (
      <Box className={classes.cellContent}>
        <StyledSelect
          value={row.driverID || ''}
          data-driver-row={row.tripLegID}
          onKeyDown={(event: React.KeyboardEvent) => {
            if (event.key === 'Tab' && !event.shiftKey) {
              const nextInput = document.querySelector(
                `[data-timefield-row="${row.tripLegID}"][data-timefield-name="onSite"] input`,
              ) as HTMLInputElement;
              if (nextInput) {
                event.preventDefault();
                event.stopPropagation();
                nextInput.focus();
              }
            }
          }}
          onChange={(event) => {
            dispatch(
              setExceptions(
                [row.tripLegID],
                providerId,
                exception?.tripLegExceptionID!,
              ),
            );
            updateTripLegDriver(
              Number(event.target.value),
              row.tripLegReferenceID,
            );
          }}>
          {drivers.items.map((driver) => (
            <StyledMenuItem key={driver.driverID} value={driver.driverID}>
              {driver.driverFirstName} {driver.driverLastName}
            </StyledMenuItem>
          ))}
        </StyledSelect>
      </Box>
    );
  };

  const isUpdatingCurrentTimeField = (
    tripLegReferenceID: string,
    field: string,
  ): boolean => {
    if (updatingTripLegLogID === tripLegReferenceID) {
      if (
        field === 'onSite' &&
        updatingTripLegLogStatus === TripLegStatusBC.Onsite
      ) {
        return true;
      }
      if (
        field === 'onBoard' &&
        updatingTripLegLogStatus === TripLegStatusBC.Onboard
      ) {
        return true;
      }
      if (
        field === 'completed' &&
        updatingTripLegLogStatus === TripLegStatusBC.Completed
      ) {
        return true;
      }
    }
    return false;
  };

  const renderTime = (params: GridCellParams): ReactElement => {
    const { row, field } = params;
    const time = params.value
      ? DateTime.fromISO(params.value as string, {
          zone: params.row.pickUpAddressTimeZoneIANA,
        })
      : undefined;
    const canceledLateTime = row.canceledLateTimeStamp
      ? DateTime.fromISO(row.canceledLateTimeStamp, {
          zone: params.row.pickUpAddressTimeZoneIANA,
        })
      : undefined;
    const noLoadTime = row.noLoadTimeStamp
      ? DateTime.fromISO(row.noLoadTimeStamp, {
          zone: params.row.pickUpAddressTimeZoneIANA,
        })
      : undefined;
    const tripLegStatus = row.tripLegStatusBC;

    const getLabel = (): string | null => {
      if (
        tripStatusNoLoadList.includes(tripLegStatus as TripLegStatusBC) &&
        noLoadTime &&
        field === 'onBoard'
      ) {
        return t('tripLegStatus.noLoad');
      }
      if (
        tripLegStatus === TripLegStatusBC.CanceledLate &&
        canceledLateTime &&
        field === 'onSite'
      ) {
        return t('tripLegStatus.canceledLate');
      }
      return null;
    };

    if (
      field === 'completed' &&
      (tripLegStatus === TripLegStatusBC.CanceledLate ||
        tripStatusNoLoadList.includes(tripLegStatus as TripLegStatusBC))
    ) {
      return <></>;
    }

    if (field === 'onBoard' && tripLegStatus === TripLegStatusBC.CanceledLate) {
      return <></>;
    }

    const isUpdating = isUpdatingCurrentTimeField(
      params.row.tripLegReferenceID,
      field,
    );

    let value = time;
    if (
      field === 'onBoard' &&
      tripStatusNoLoadList.includes(tripLegStatus as TripLegStatusBC)
    ) {
      value = noLoadTime;
    }
    if (
      field === 'onSite' &&
      tripLegStatus === TripLegStatusBC.CanceledLate &&
      canceledLateTime
    ) {
      value = canceledLateTime;
    }

    const label = getLabel();

    const timeFieldOrder = ['onSite', 'onBoard', 'completed'];

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (
        event.key === 'ArrowLeft' ||
        event.key === 'ArrowRight' ||
        event.key === 'ArrowUp' ||
        event.key === 'ArrowDown'
      ) {
        event.stopPropagation();
      }

      if (event.key === 'Tab') {
        const currentIndex = timeFieldOrder.indexOf(field);
        const isShiftTab = event.shiftKey;
        const nextIndex = isShiftTab ? currentIndex - 1 : currentIndex + 1;

        if (nextIndex >= 0 && nextIndex < timeFieldOrder.length) {
          const nextField = timeFieldOrder[nextIndex];
          const rowId = params.row.tripLegID;

          const nextInput = document.querySelector(
            `[data-timefield-row="${rowId}"][data-timefield-name="${nextField}"] input`,
          ) as HTMLInputElement;

          if (nextInput && !nextInput.disabled) {
            event.preventDefault();
            event.stopPropagation();
            nextInput.focus();
          }
        } else if (isShiftTab && currentIndex === 0) {
          const rowId = params.row.tripLegID;
          const driverSelect = document.querySelector(
            `[data-driver-row="${rowId}"] .MuiSelect-select`,
          ) as HTMLElement;
          if (driverSelect) {
            event.preventDefault();
            event.stopPropagation();
            driverSelect.focus();
          }
        }
      }
    };

    return (
      <Box className={classes.cellContent}>
        {label && (
          <Typography
            className={classes.primaryText}
            sx={{ textAlign: 'center', width: '100%' }}>
            {label}
          </Typography>
        )}
        <Typography
          className={`${classes.renderTime} ${label ? 'has-label' : ''}`}>
          <TimeFieldCell
            key={`${params.row.tripLegID}-${field}-${
              cellResetCounters.get(`${params.row.tripLegID}-${field}`) ?? 0
            }`}
            value={value}
            data-timefield-row={params.row.tripLegID}
            data-timefield-name={field}
            slotProps={{
              textField: {
                onKeyDown: handleKeyDown,
                inputProps: {
                  'data-timefield-row': params.row.tripLegID,
                  'data-timefield-name': field,
                },
              },
            }}
            onBlur={(event) => {
              const newTime = event.target.value;
              const isPlaceholder = newTime === 'hh:mm aa';
              const hasTimeEntered = newTime && !isPlaceholder;

              // Skip update if value hasn't changed
              const currentFormatted = value ? value.toFormat('hh:mm a') : '';
              if (newTime === currentFormatted || (!hasTimeEntered && !value)) {
                return;
              }

              // Clear any existing error when the user enters a new time
              if (hasTimeEntered) {
                dispatch(setTripLegError(null));
                lastCommittedLogsRef.current = [];
              }

              const resetField = () => {
                setCellResetCounters((prev) => {
                  const next = new Map(prev);
                  const cellKey = `${params.row.tripLegID}-${field}`;
                  next.set(cellKey, (prev.get(cellKey) ?? 0) + 1);
                  return next;
                });
              };

              // Validate that previous status is filled before allowing update
              // Only show error if user actually entered a time
              if (hasTimeEntered && field === 'onBoard' && row.onSite === '') {
                resetField();
                dispatch(
                  setTripLegError(t('needsAttention.onSiteRequiredFirst')),
                );
                return;
              }
              if (
                hasTimeEntered &&
                field === 'completed' &&
                (row.onSite === '' || row.onBoard === '')
              ) {
                resetField();
                dispatch(
                  setTripLegError(
                    t('needsAttention.previousTimestampsRequired'),
                  ),
                );
                return;
              }

              // Validate chronological order for regular trips
              if (
                hasTimeEntered &&
                tripLegStatus !== TripLegStatusBC.NoLoad &&
                tripLegStatus !== TripLegStatusBC.CanceledLate
              ) {
                const date = params.row.appointmentDate;
                const zone = params.row.pickUpAddressTimeZoneIANA;
                const newDateTime = DateTime.fromFormat(
                  `${date} ${newTime}`,
                  'yyyy-MM-dd h:mm a',
                  { zone },
                );

                if (field === 'onBoard' && row.onSite) {
                  const onSiteDateTime = DateTime.fromISO(row.onSite, {
                    zone,
                  });
                  if (
                    newDateTime.isValid &&
                    onSiteDateTime.isValid &&
                    newDateTime < onSiteDateTime
                  ) {
                    resetField();
                    dispatch(
                      setTripLegError(t('needsAttention.onBoardBeforeOnSite')),
                    );
                    return;
                  }
                }

                if (field === 'completed' && row.onBoard) {
                  const onBoardDateTime = DateTime.fromISO(row.onBoard, {
                    zone,
                  });
                  if (
                    newDateTime.isValid &&
                    onBoardDateTime.isValid &&
                    newDateTime < onBoardDateTime
                  ) {
                    resetField();
                    dispatch(
                      setTripLegError(
                        t('needsAttention.completedBeforeOnBoard'),
                      ),
                    );
                    return;
                  }
                }
              }

              // Validate NoLoad time is not earlier than OnSite time
              if (
                hasTimeEntered &&
                tripStatusNoLoadList.includes(tripLegStatus as TripLegStatusBC)
              ) {
                const date = params.row.appointmentDate;
                const zone = params.row.pickUpAddressTimeZoneIANA;
                const newDateTime = DateTime.fromFormat(
                  `${date} ${newTime}`,
                  'yyyy-MM-dd h:mm a',
                  { zone },
                );

                if (field === 'onBoard' && row.onSite) {
                  const onSiteDateTime = DateTime.fromISO(row.onSite, {
                    zone,
                  });
                  if (
                    newDateTime.isValid &&
                    onSiteDateTime.isValid &&
                    newDateTime <= onSiteDateTime
                  ) {
                    resetField();
                    dispatch(
                      setTripLegError(
                        t('needsAttention.noLoadTimeBeforeOnSite'),
                      ),
                    );
                    return;
                  }
                }

                if (field === 'onSite' && noLoadTime) {
                  if (newDateTime.isValid && newDateTime >= noLoadTime) {
                    resetField();
                    dispatch(
                      setTripLegError(
                        t('needsAttention.noLoadTimeBeforeOnSite'),
                      ),
                    );
                    return;
                  }
                }
              }

              // Process the timestamp update using the utility function
              const result = processTimestampUpdate({
                newTime: event.target.value,
                field,
                tripLeg: params.row,
                currentValue: value,
                canceledLateTime,
                noLoadTime,
              });

              if (result.isValid && result.isoTimestamp && result.fieldName) {
                // Clear any existing error message when valid input is entered
                dispatch(setTripLegError(null));

                // Update optimistic state using the correct key for remapped fields
                const optimisticField =
                  result.fieldName === TripLegStatusBC.NoLoad
                    ? 'noLoad'
                    : result.fieldName === TripLegStatusBC.CanceledLate
                    ? 'canceledLate'
                    : field;
                if (
                  lastOptimisticTripRef.current !==
                  params.row.tripLegReferenceID
                ) {
                  lastCommittedLogsRef.current = [];
                }
                lastOptimisticTripRef.current = params.row.tripLegReferenceID;
                lastCommittedLogsRef.current = [
                  ...lastCommittedLogsRef.current,
                  {
                    tripLegReferenceID: params.row.tripLegReferenceID,
                    status: result.fieldName as TripLegStatusBC,
                  },
                ];
                updateOptimisticTimestamp(
                  params.row.tripLegReferenceID,
                  optimisticField,
                  result.isoTimestamp,
                );

                // Set exception optimistically to prevent race condition with status update
                if (exception?.tripLegExceptionID) {
                  dispatch(
                    upsertTripLegException({
                      tripLegID: params.row.tripLegID,
                      tripLegExceptionID: exception.tripLegExceptionID,
                    }),
                  );
                }
                dispatch(
                  setExceptions(
                    [params.row.tripLegID],
                    providerId,
                    exception?.tripLegExceptionID!,
                  ),
                );

                if (result.fieldName === TripLegStatusBC.Completed) {
                  dispatch(
                    overrideCompletedStatusAndUpdateLog(
                      params.row.passengerUUID,
                      params.row.tripLegReferenceID,
                      result.isoTimestamp,
                      providerId,
                      result.timestampExists!,
                      params.row.tripLegStatusBC as TripLegStatusBC,
                    ),
                  );
                } else {
                  dispatch(
                    updateTripLegLogAsync(
                      result.fieldName as TripLegStatusBC,
                      params.row.passengerUUID,
                      params.row.tripLegReferenceID,
                      result.isoTimestamp,
                      providerId,
                      result.timestampExists!,
                      params.row.tripLegStatusBC as TripLegStatusBC,
                    ),
                  );
                }
              }
            }}
          />
        </Typography>
        {isUpdating && <LinearProgress />}
      </Box>
    );
  };

  const renderCenterHeader = ({
    colDef: { headerName },
  }: {
    colDef: { headerName?: string };
  }): ReactNode => {
    return (
      <Box
        style={{
          textAlign: 'center',
          width: '100%',
          lineHeight: '100%',
          height: '1',
        }}>
        {headerName}
      </Box>
    );
  };

  const renderPrice = (params: GridCellParams): ReactElement => {
    return <PriceTooltip params={params as any} classes={classes} />;
  };

  const columns: GridColDef<TripLegAttestationItem>[] = [
    {
      field: 'pickupTime',
      headerName: t('needsAttention.pickup'),
      flex: 1,
      renderCell: renderPickupTime,
    },
    {
      field: 'member',
      headerName: t('generateInvoice.member'),
      flex: 1,
      renderCell: renderMember,
    },
    {
      field: 'pickupAddress',
      headerName: t('needsAttention.pickupAddress'),
      flex: 1,
      renderCell: renderPickupAddress,
    },
    {
      field: 'dropoffAddress',
      headerName: t('needsAttention.dropoffAddress'),
      flex: 1,
      renderCell: renderDropoffAddress,
    },
    {
      field: 'driver',
      headerName: t('needsAttention.driver'),
      width: 160,
      renderCell: renderDriver,
    },
    {
      field: 'onSite',
      headerName: t('tripLegStatus.onSite'),
      renderCell: renderTime,
      width: 100,
      renderHeader: (params) => renderCenterHeader(params),
      sortable: false,
    },
    {
      field: 'onBoard',
      headerName: t('tripLegStatus.onBoard'),
      renderCell: renderTime,
      width: 100,
      align: 'center',
      renderHeader: (params) => renderCenterHeader(params),
      sortable: false,
    },
    {
      field: 'completed',
      headerName: t('tripLegStatus.completed'),
      renderCell: renderTime,
      width: 100,
      align: 'center',
      renderHeader: (params) => renderCenterHeader(params),
      sortable: false,
    },
    {
      field: 'price',
      headerName: t('needsAttention.price'),
      width: 92,
      renderCell: renderPrice,
    },
    {
      field: 'exception',
      headerName: t('needsAttention.exception'),
    },
    {
      field: 'actions',
      headerName: ' ',
      width: 214,
      sortable: false,
      renderCell: (params) => (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              height: '100%',
              width: '100%',
            }}>
            <IconButton
              onClick={(e) => openMenu(e, params.row.tripLegID, params.row)}>
              <MoreVertIcon />
            </IconButton>
          </Box>
          <StyledMenu
            anchorEl={anchorEl}
            open={Boolean(anchorEl) && menuOpenRowId === params.row.tripLegID}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            onClose={closeMenu}
            elevation={1}>
            <MenuItem
              disabled={
                !params.row.isUnderDiscussion &&
                (params.row.exception === 'Missing Info' ||
                  checkTimestampsFilled(params.row))
              }
              onClick={() => {
                handleReadyToInvoice(params.row.tripLegID);
                closeMenu();
              }}>
              {t('needsAttention.readyToInvoice')}
            </MenuItem>
            <MenuItem
              disabled={tripStatusNoLoadList.includes(
                params.row.tripLegStatusBC as TripLegStatusBC,
              )}
              onClick={() => {
                setSelectedTripLegStatus(params.row);
                setOriginalStatus(
                  params.row.tripLegStatusBC as TripLegStatusBC,
                );
                setTargetStatus(TripLegStatus.NoLoad);
                setStatusDialogOpen(true);
                closeMenu();
              }}>
              {t('tripLegStatus.noLoad')}
            </MenuItem>
            <MenuItem
              disabled={
                params.row.tripLegStatusBC === TripLegStatusBC.CanceledLate
              }
              onClick={() => {
                setSelectedTripLegStatus(params.row);
                setOriginalStatus(
                  params.row.tripLegStatusBC as TripLegStatusBC,
                );
                setTargetStatus(TripLegStatus.CanceledLate);
                setStatusDialogOpen(true);
                closeMenu();
              }}>
              {t('tripLegStatus.canceledLate')}
            </MenuItem>
            <MenuItem
              disabled={
                params.row.tripLegStatusBC !== TripLegStatusBC.CanceledLate &&
                params.row.tripLegStatusBC !== TripLegStatusBC.NoLoad
              }
              onClick={() => {
                setSelectedTripLegStatus(params.row);
                setOriginalStatus(
                  params.row.tripLegStatusBC as TripLegStatusBC,
                );
                setTargetStatus(TripLegStatus.Completed);
                setStatusDialogOpen(true);
                closeMenu();
              }}>
              {t('tripLegStatus.completed')}
            </MenuItem>
            <MenuItem
              onClick={() => {
                setdiscussionDialogOpen(true);
                closeMenu();
              }}>
              {t('needsAttention.discuss')}
            </MenuItem>
          </StyledMenu>
        </>
      ),
    },
  ];

  const handleClose = () => {
    setPage(1);
    setSelectedTrips([]);
    onClose();
  };

  return (
    <>
      <StyledDialog
        fullScreen={true}
        open={open}
        onClose={handleClose}
        keepMounted={true}
        TransitionComponent={DialogTransition}>
        <FullScreenDialogTitle
          title={t('needsAttention.title')}
          close={handleClose}
        />
        <FullScreenDialogContent>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <Box className={classes.container}>
              <Paper elevation={3}>
                <Box className={classes.gridContainer}>
                  <Box className={classes.toolbar}>
                    <Box className={classes.toolContainer}>
                      <OutlinedInput
                        size="small"
                        className={classes.toolInput}
                        sx={{ width: '327px' }}
                        placeholder={t('needsAttention.search')}
                        value={searchQuery}
                        onChange={handleSearchChange}
                        startAdornment={
                          <InputAdornment position="start">
                            <IconButton edge="start">
                              <Search />
                            </IconButton>
                          </InputAdornment>
                        }
                      />
                      <Select
                        className={classes.toolInput}
                        defaultValue="View all"
                        sx={{ width: '180px' }}
                        IconComponent={ExpandMore}
                        value={exceptionFilter}
                        onChange={handleExceptionFilterChange}>
                        <MenuItem value="View all">
                          {t('needsAttention.viewAll')}
                        </MenuItem>
                        <MenuItem value="Under Discussion">
                          {t('needsAttention.underDiscussion')}
                        </MenuItem>
                        <MenuItem value="Missing Info">
                          {t('needsAttention.missingInfo')}
                        </MenuItem>
                        <MenuItem value="Ready to Invoice">
                          {t('needsAttention.readyToInvoice')}
                        </MenuItem>
                      </Select>
                    </Box>
                    {selectedTrips.length > 0 && (
                      <Box className={classes.toolContainer}>
                        <Button
                          variant="text"
                          color="secondary"
                          onClick={() => setSelectedTrips([])}>
                          <Typography className={classes.primaryText}>
                            {selectedTrips.length}{' '}
                            {t('needsAttention.selected')}
                          </Typography>
                          <StyledCancelOutlined />
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          className={classes.moveToAttestationButton}
                          onClick={() => {
                            dispatch(
                              clearException(selectedTrips, providerId, () => {
                                dispatch(
                                  fetchTripLegsV2Async({
                                    transportationProviderID: [
                                      providerId.toString(),
                                    ],
                                    attested: false,
                                    includeTripLegStatusLogs: true,
                                    includeTripLegAddOnLineItems: true,
                                    page: 1,
                                    pageSize: 1000,
                                    tripLegStatusBCs: [
                                      ...tripStatusAttestationList,
                                    ],
                                    pickupStartDate: attestationUtils
                                      .getStartDate()
                                      .toUTC()
                                      .toISO(),
                                    pickupEndDate: DateTime.now()
                                      .endOf('day')
                                      .toUTC()
                                      .toISO(),
                                  }),
                                );
                              }),
                            );
                            setSelectedTrips([]);
                            closeMenu();
                          }}>
                          {t('needsAttention.moveToInvoice')}
                        </Button>
                      </Box>
                    )}
                  </Box>
                  <DataGridPro
                    rows={paginatedTrips}
                    columns={columns}
                    getRowClassName={(params) => {
                      if (params.row.tripLegID === menuOpenRowId) {
                        return classes.rowHover;
                      }
                      return '';
                    }}
                    className={classes.grid}
                    checkboxSelection
                    isRowSelectable={canSelectRow}
                    disableRowSelectionOnClick
                    rowSelectionModel={selectedTrips}
                    onRowSelectionModelChange={(model) => {
                      const currentPageIds = new Set(
                        paginatedTrips.map((row) => row.tripLegID),
                      );
                      const previouslySelectedTrips = selectedTrips.filter(
                        (id) => !currentPageIds.has(id),
                      );
                      const uniqueSelections = new Set([
                        ...previouslySelectedTrips,
                        ...(model as number[]),
                      ]);
                      setSelectedTrips(Array.from(uniqueSelections));
                    }}
                    hideFooterSelectedRowCount
                    rowHeight={56}
                    disableColumnMenu={true}
                    disableColumnSorting={true}
                    hideFooter
                  />
                  <TablePagination
                    component="div"
                    count={filteredTrips.length}
                    page={page - 1}
                    onPageChange={(event, page) => setPage(page + 1)}
                    rowsPerPage={pageSize}
                    onRowsPerPageChange={(event) => {
                      setPageSize(Number.parseInt(event.target.value, 10));
                      setPage(1);
                    }}
                  />
                </Box>
              </Paper>
              <Box className={classes.bottomButtonContainer}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleClose}
                  className={classes.bottomCloseButton}>
                  {t('common.close')}
                </Button>
              </Box>
            </Box>
          </LocalizationProvider>
          <StatusDialog
            selectedTripLeg={selectedTripLegStatus}
            open={statusDialogOpen}
            onClose={() => setStatusDialogOpen(false)}
            providerId={providerId ?? 0}
            originalStatus={originalStatus ?? null}
            targetStatus={targetStatus}
            onStatusChanged={(tripLegReferenceID, newStatus, timestamps) => {
              // Set optimistic status change using the hook
              updateOptimisticStatus(tripLegReferenceID, newStatus, timestamps);
            }}
          />
        </FullScreenDialogContent>
        <DiscussionDialog
          open={discussionDialogOpen}
          onClose={() => setdiscussionDialogOpen(false)}
          tripLeg={selectedTripLeg}
          providerId={providerId ?? 0}
          page={page}
          pageSize={pageSize}
        />
      </StyledDialog>
      <Snackbar
        open={!!tripLegError}
        onClose={() => dispatch(setTripLegError(null))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert variant="filled" severity="error">
          {tripLegError}
        </Alert>
      </Snackbar>
    </>
  );
}

export default memo(NeedsAttentionDialog);
